import type * as Sentry from "@sentry/core";
import type { App } from "vue";

import { mergeDeepRight } from "ramda";

import { getEnv } from "./solvariEnv";

type AddDataToProject = () => Pick<Sentry.Event, "contexts" | "tags">;

type CaptureArgs = Parameters<typeof Sentry.captureException>;
type CaptureException = (...args: CaptureArgs) => void;

type GlobalSentry = Partial<{
  captureException: CaptureException;
  captureLog: CaptureException;
  trackVueErrors: (app: App) => void;
}>;
type SentryInstance = Required<GlobalSentry>;

declare global {
  var sentry: GlobalSentry | undefined;

  var sentryProjects: Record<string, AddDataToProject> | undefined;
}

/*
  Gets the global Sentry instance, this avoids having to include Sentry in every micro frontend
 */
function getSentry(instanceContext?: CaptureArgs[1]): SentryInstance {
  type Context = Exclude<CaptureArgs[1], undefined>;
  function mergeContext(captureContext?: Context): Context | undefined {
    if (!instanceContext || !captureContext) {
      return instanceContext ?? captureContext;
    }
    return mergeDeepRight(
      instanceContext as Record<string, unknown>,
      captureContext as Record<string, unknown>,
    );
  }

  return {
    captureException: (error: CaptureArgs[0], hint?: CaptureArgs[1]) => {
      const context = mergeContext(hint);
      globalThis.sentry?.captureException?.(error, context);
      if (getEnv().config.envType !== "production") {
        console.error(error, context);
      }
    },
    captureLog: (error: CaptureArgs[0], hint?: CaptureArgs[1]) => {
      const context = mergeContext(hint);
      globalThis.sentry?.captureLog?.(error, context);
      if (getEnv().config.envType !== "production") {
        console.warn(error, context);
      }
    },
    trackVueErrors: globalThis.sentry?.trackVueErrors ?? trackVueErrorsFallback,
  };
}

function addDataToSentryProject(
  project:
    | "argus-fe"
    | "company-registration-form"
    | "contact-form"
    | "lead-forms"
    | "mkt-ab-testing",
  addData: AddDataToProject,
) {
  globalThis.sentryProjects = globalThis.sentryProjects ?? {};
  globalThis.sentryProjects[project] = addData;
}

function trackVueErrorsFallback(vueApp: App) {
  vueApp.config.errorHandler = (error) => getSentry().captureException(error);
}

export { addDataToSentryProject, getSentry };
export type { AddDataToProject, CaptureException, SentryInstance };
